import { isNil } from 'es-toolkit';

import type { TrackEventInput } from '@savvy/constants/generated/gql-types';
import { graphql as tadaGraphql } from '@savvy/deprecated-shared/gql-compat';
import { useRoutePattern } from '@savvy/deprecated-shared/hooks/use-route-pattern';
import { useRouter } from '@savvy/deprecated-shared/hooks/use-router';
import { useMutationWithErrorLogging } from '@savvy/deprecated-shared/hooks/useMutationWithErrorLogging';

import type { TrackableRoute } from './utils';
import { useHousehold } from '../contexts/household-context';
import { mapRouteToPageName } from './utils';

const UseTrackEventMutation = tadaGraphql(/* GraphQL */ `
  mutation UseTrackEvent($input: TrackEventInput!) {
    trackEvent(input: $input) {
      __typename
    }
  }
`);

/**
 * Track an event in MixPanel. Runs asynchronously and has a low chance of
 * failing without retries due to using raw `POST` under-the-hood. This is a
 * okay tradeoff for a method we'll be calling relatively frequently that does
 * not need to be 100% reliable
 *
 * @param event - The event to track
 */
export function useTrackEvent<Event extends keyof TrackEventInput>(
  event: Event,
): (data: Omit<NonNullable<TrackEventInput[Event]>, 'pageName' | 'route' | 'urlQueryParams'>) => void {
  const { household } = useHousehold();
  const router = useRouter();
  const [mutation] = useMutationWithErrorLogging(UseTrackEventMutation);
  const routePattern = useRoutePattern();
  // eslint-disable-next-line typescript/consistent-type-assertions -- FIXME: This rule was recently enabled for the repo
  const pageName = mapRouteToPageName(routePattern as TrackableRoute);
  if (isNil(pageName)) {
    return () => {
      /* empty */
    };
  }

  return (data?: Omit<NonNullable<TrackEventInput[Event]>, 'pageName' | 'route' | 'urlQueryParams'>) => {
    mutation({
      variables: {
        input: {
          [event]: {
            ...data,
            pageName,
            route: router.pathname,
            urlQueryParams: router.query,
            isDemoHousehold: household?.demo ?? false,
          },
        },
      },
    });
  };
}
